import { buildTransferableSkillsLexicon, clean, uniqCI } from './transferableSkillsLexicon';

const DEFAULT_SKILL_LIMIT = 24;
const TRANSFERABLE_SKILL_RULES = [
  {
    skill: 'Records Management',
    evidence: ['record', 'case file', 'filing', 'document control', 'intake'],
    target: ['record', 'case file', 'documentation', 'information management'],
  },
  {
    skill: 'Public Contact',
    evidence: ['public inquiries', 'customer', 'client', 'visitor', 'resident', 'phone', 'front desk'],
    target: ['public contact', 'customer', 'stakeholder', 'client', 'communication'],
  },
  {
    skill: 'Technical Support',
    evidence: ['troubleshoot', 'hardware', 'software', 'network', 'desktop support', 'help desk', 'technical issue'],
    target: ['technical support', 'troubleshoot', 'it support', 'systems support'],
  },
  {
    skill: 'Data Analysis',
    evidence: ['data', 'spreadsheet', 'report', 'analysis', 'metrics', 'trend'],
    target: ['data', 'analysis', 'reporting', 'metrics'],
  },
  {
    skill: 'Budget Analysis',
    evidence: ['budget', 'expense', 'financial', 'invoice', 'funding'],
    target: ['budget', 'financial', 'funding', 'cost'],
  },
  {
    skill: 'Compliance',
    evidence: ['policy', 'procedure', 'regulation', 'compliance', 'audit', 'quality'],
    target: ['compliance', 'policy', 'regulation', 'quality assurance'],
  },
  {
    skill: 'Project Coordination',
    evidence: ['project', 'coordinate', 'schedule', 'milestone', 'deliverable', 'implementation'],
    target: ['project', 'coordinate', 'implementation', 'deliverable'],
  },
  {
    skill: 'Leadership',
    evidence: ['supervised', 'led ', 'lead ', 'trained', 'mentor', 'team'],
    target: ['leadership', 'team', 'supervis', 'coordinate'],
  },
  {
    skill: 'Process Improvement',
    evidence: ['improved', 'streamlined', 'optimized', 'reduced', 'increased', 'workflow'],
    target: ['process improvement', 'workflow', 'efficiency', 'optimize'],
  },
  {
    skill: 'Inventory Management',
    evidence: ['inventory', 'stock', 'supplies', 'restock', 'warehouse'],
    target: ['inventory', 'logistics', 'supply'],
  },
];
const TARGET_STOP_WORDS = new Set([
  'and', 'the', 'with', 'for', 'from', 'into', 'using', 'skills', 'skill',
  'experience', 'knowledge', 'ability', 'required', 'preferred', 'including',
]);

const tokenize = (value) => {
  const text = clean(value).toLowerCase();
  if (!text) return [];
  return text
    .split(/[^a-z0-9+#.]+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2);
};

const collectContextTokens = (values = []) => {
  const tokens = new Set();
  values.forEach((value) => {
    tokenize(value).forEach((token) => tokens.add(token));
  });
  return tokens;
};

const normalizeContextValues = (context = {}) => uniqCI([
  context.jobDescription,
  context.jobSummary,
  context.experienceRequirements,
  context.educationRequirements,
  ...(Array.isArray(context.keywords) ? context.keywords : []),
  ...(Array.isArray(context.requiredSkills) ? context.requiredSkills : []),
  ...(Array.isArray(context.preferredSkills) ? context.preferredSkills : []),
  ...(Array.isArray(context.softSkills) ? context.softSkills : []),
  ...(Array.isArray(context.competencies) ? context.competencies : []),
  ...(Array.isArray(context.knowledge) ? context.knowledge : []),
]);

const textIncludesAny = (text, terms = []) => terms.some((term) => text.includes(term));

const meaningfulTokens = (value) => tokenize(value)
  .filter((token) => !TARGET_STOP_WORDS.has(token));

export const scoreSkillTargetRelevance = (skill, context = {}) => {
  const normalizedSkill = clean(skill);
  if (!normalizedSkill) return 0;

  const targetValues = normalizeContextValues(context);
  const targetText = targetValues.join(' ').toLowerCase();
  const skillLower = normalizedSkill.toLowerCase();
  if (!targetText) return 0;
  if (targetText.includes(skillLower)) return 100;

  const skillTokens = meaningfulTokens(normalizedSkill);
  const targetTokens = collectContextTokens(targetValues);
  if (!skillTokens.length) return 0;
  const matches = skillTokens.filter((token) => targetTokens.has(token)).length;
  const requiredMatches = skillTokens.length === 1
    ? 1
    : Math.max(2, Math.ceil(skillTokens.length * 0.6));
  return matches >= requiredMatches ? matches * 10 : 0;
};

export const scoreSkillRelevance = (skill, context = {}, formData = {}) => {
  const normalizedSkill = clean(skill);
  if (!normalizedSkill) return 0;

  const skillTokens = tokenize(normalizedSkill);
  const skillLower = normalizedSkill.toLowerCase();
  const targetValues = normalizeContextValues(context);
  const targetText = targetValues.join(' ').toLowerCase();
  const targetTokens = collectContextTokens(targetValues);
  const resumeLexicon = buildTransferableSkillsLexicon(formData);
  const evidenceText = uniqCI([
    ...resumeLexicon.academics,
    ...resumeLexicon.workExperience,
    ...resumeLexicon.volunteerExperience,
    ...resumeLexicon.military,
    ...resumeLexicon.credentials,
    ...resumeLexicon.additionalExperience,
  ]).join(' ').toLowerCase();

  let score = 0;
  if (targetText.includes(skillLower)) score += 12;
  if (evidenceText.includes(skillLower)) score += 4;
  skillTokens.forEach((token) => {
    if (targetTokens.has(token)) score += 3;
    if (evidenceText.includes(token)) score += 1;
  });

  return score;
};

export const sortSkillsByRelevance = (skills = [], context = {}, formData = {}) => (
  uniqCI(skills)
    .map((skill, index) => ({
      skill,
      index,
      score: scoreSkillRelevance(skill, context, formData),
    }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.skill)
);

export const optimizeSkillsForResume = (
  skills = [],
  context = {},
  formData = {},
  limit = DEFAULT_SKILL_LIMIT,
) => {
  const sorted = sortSkillsByRelevance(skills, context, formData);
  const scored = sorted.map((skill) => ({
    skill,
    score: scoreSkillRelevance(skill, context, formData),
  }));
  const relevant = scored.filter((entry) => entry.score > 0).map((entry) => entry.skill);
  const fallback = scored.map((entry) => entry.skill);
  return (relevant.length ? relevant : fallback).slice(0, limit);
};

export const curateOneShotSkills = ({
  generatedSkills = [],
  profileSkills = [],
  context = {},
  limit = DEFAULT_SKILL_LIMIT,
} = {}) => uniqCI([
  ...generatedSkills,
  ...profileSkills,
])
  .map((skill, index) => ({
    skill,
    index,
    score: scoreSkillTargetRelevance(skill, context),
  }))
  .filter((entry) => entry.score > 0)
  .sort((a, b) => b.score - a.score || a.index - b.index)
  .slice(0, limit)
  .map((entry) => entry.skill);

export const inferTransferableSkillsForOneShot = (
  formData = {},
  context = {},
  limit = 12,
) => {
  const resumeLexicon = buildTransferableSkillsLexicon(formData);
  const evidenceText = uniqCI([
    ...resumeLexicon.academics,
    ...resumeLexicon.workExperience,
    ...resumeLexicon.volunteerExperience,
    ...resumeLexicon.military,
    ...resumeLexicon.credentials,
    ...resumeLexicon.additionalExperience,
    ...resumeLexicon.existingSkills,
  ]).join(' ').toLowerCase();
  const targetText = normalizeContextValues(context).join(' ').toLowerCase();
  if (!evidenceText || !targetText) return [];

  return TRANSFERABLE_SKILL_RULES
    .filter((rule) => (
      textIncludesAny(evidenceText, rule.evidence)
      && (
        textIncludesAny(targetText, rule.target)
        || scoreSkillTargetRelevance(rule.skill, context) > 0
      )
    ))
    .map((rule) => rule.skill)
    .slice(0, limit);
};

export const randomizeSkills = (skills = []) => {
  const shuffled = uniqCI(skills);
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
};

export const buildSkillContextFromForm = (formData = {}, extra = {}) => ({
  jobDescription: extra.jobDescription || formData.oneShotJobDescription,
  jobSummary: extra.jobSummary || formData.oneShotJobAnalysis?.jobSummary || formData.jobSummary,
  experienceRequirements: (
    extra.experienceRequirements
    || formData.oneShotJobAnalysis?.experienceRequirements
    || formData.experienceRequirements
    || formData.specializedExperience
  ),
  educationRequirements: (
    extra.educationRequirements
    || formData.oneShotJobAnalysis?.educationRequirements
    || formData.educationRequirements
  ),
  keywords: uniqCI([
    ...(Array.isArray(extra.keywords) ? extra.keywords : []),
    ...(Array.isArray(formData.oneShotJobAnalysis?.keywords) ? formData.oneShotJobAnalysis.keywords : []),
    ...(Array.isArray(formData.oneShotJobAnalysis?.jobKeywords) ? formData.oneShotJobAnalysis.jobKeywords : []),
    ...(Array.isArray(formData.jobKeywords) ? formData.jobKeywords : []),
    ...(Array.isArray(formData.selectedJobKeywords) ? formData.selectedJobKeywords : []),
    ...(Array.isArray(formData.keywords) ? formData.keywords : []),
  ]),
  requiredSkills: uniqCI([
    ...(Array.isArray(extra.requiredSkills) ? extra.requiredSkills : []),
    ...(Array.isArray(formData.oneShotJobAnalysis?.requiredSkills) ? formData.oneShotJobAnalysis.requiredSkills : []),
    ...(Array.isArray(formData.oneShotJobAnalysis?.Knowledge) ? formData.oneShotJobAnalysis.Knowledge : []),
    ...(Array.isArray(formData.requiredSkills) ? formData.requiredSkills : []),
    ...(Array.isArray(formData.knowledge) ? formData.knowledge : []),
  ]),
  preferredSkills: uniqCI([
    ...(Array.isArray(extra.preferredSkills) ? extra.preferredSkills : []),
    ...(Array.isArray(formData.oneShotJobAnalysis?.preferredSkills) ? formData.oneShotJobAnalysis.preferredSkills : []),
    ...(Array.isArray(formData.preferredSkills) ? formData.preferredSkills : []),
  ]),
  softSkills: uniqCI([
    ...(Array.isArray(extra.softSkills) ? extra.softSkills : []),
    ...(Array.isArray(formData.oneShotJobAnalysis?.softSkills) ? formData.oneShotJobAnalysis.softSkills : []),
    ...(Array.isArray(formData.oneShotJobAnalysis?.Competencies) ? formData.oneShotJobAnalysis.Competencies : []),
    ...(Array.isArray(formData.softSkills) ? formData.softSkills : []),
    ...(Array.isArray(formData.competencies) ? formData.competencies : []),
  ]),
  signals: extra.signals,
});

export { DEFAULT_SKILL_LIMIT };
